import { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { fetchProjectWorklogs, fetchProjectsListSimple } from "./ddApi";
import { extractTime, findProjectForTender, projectDisplayName } from "./worklogHelpers";
import { ClockIcon, CloseIcon } from "./WorkLogShared";
import "./taskPlanner.css";

const PERIODS = [
  { value: "overall", label: "Overall" },
  { value: "monthly", label: "Monthly" },
  { value: "weekly", label: "Weekly" },
];

const formatLogDate = (log) => {
  const raw = log?.date || log?.start_time || "";
  if (!raw) return "—";
  return String(raw).includes("T") ? String(raw).split("T")[0] : String(raw).slice(0, 10);
};

const formatLogUser = (log) => {
  const name = log?.user_name || "";
  const code = log?.emp_code || "";
  if (name && code) return `${name} (${code})`;
  return name || code || "—";
};

const formatLogHours = (log) => {
  if (typeof log?.hours === "number") return `${log.hours.toFixed(2)} hrs`;
  return "—";
};

const formatClock = (value) => {
  const raw = extractTime(value);
  if (!raw) return "—";
  const match = String(raw).match(/(\d{2}:\d{2})/);
  return match ? match[1] : raw;
};

const ViewWorkLogModal = ({
  isOpen,
  onClose,
  tender = null,
  projects = [],
}) => {
  const [internalProjects, setInternalProjects] = useState([]);
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState({ total_worklogs: 0, total_hours: 0, project_name: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [period, setPeriod] = useState("overall");

  const projectList = projects.length ? projects : internalProjects;
  const matchedProject = findProjectForTender(tender, projectList);
  const projectId = tender?.id ? String(tender.id) : "";
  const tenderLabel =
    summary.project_name ||
    tender?.tender_name ||
    tender?.name ||
    projectDisplayName(matchedProject) ||
    "this tender";

  useEffect(() => {
    if (!isOpen) {
      setPeriod("overall");
      return undefined;
    }
    if (projects.length) return undefined;
    let cancelled = false;
    fetchProjectsListSimple()
      .then((list) => {
        if (!cancelled) setInternalProjects(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!cancelled) setInternalProjects([]);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, projects.length]);

  useEffect(() => {
    if (!isOpen) {
      setLogs([]);
      setSummary({ total_worklogs: 0, total_hours: 0, project_name: "" });
      setError("");
      setLoading(false);
      return undefined;
    }
    if (!projectId) {
      setLogs([]);
      setError("Tender id is missing.");
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError("");
    fetchProjectWorklogs({ projectId, period })
      .then((data) => {
        if (cancelled) return;
        setLogs(Array.isArray(data.worklogs) ? data.worklogs : []);
        setSummary({
          total_worklogs: data.total_worklogs || 0,
          total_hours: data.total_hours || 0,
          project_name: data.project_name || "",
        });
      })
      .catch((err) => {
        if (cancelled) return;
        setLogs([]);
        setSummary({ total_worklogs: 0, total_hours: 0, project_name: "" });
        setError(
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "Failed to load work logs"
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, projectId, period]);

  return (
    <Modal
      show={isOpen}
      onHide={onClose}
      centered
      backdrop="static"
      className="tp-worklog-overlay"
      dialogClassName="tp-view-dialog"
      contentClassName="tp-worklog-content"
      backdropClassName="tp-worklog-backdrop"
    >
      <div className="tp-modal-head">
        <div className="tp-modal-title-wrap">
          <span className="tp-modal-icon"><ClockIcon /></span>
          <div>
            <h3>View Work Logs</h3>
            <p>{tenderLabel}</p>
          </div>
        </div>
        <div className="tp-modal-head-right">
          <div className="tp-period-pills">
            {PERIODS.map((item) => (
              <button
                key={item.value}
                type="button"
                className={`tp-period-pill ${period === item.value ? "is-active" : ""}`}
                onClick={() => setPeriod(item.value)}
              >
                {item.label}
              </button>
            ))}
          </div>
          <button type="button" className="tp-close-btn" onClick={onClose} title="Close">
            <CloseIcon />
          </button>
        </div>
      </div>

      <div className="tp-modal-body">
        {loading ? (
          <div className="tp-modal-loader">
            <span className="tp-modal-spinner" aria-hidden="true" />
            <p>Loading work logs…</p>
          </div>
        ) : error ? (
          <div className="tp-error-banner">{error}</div>
        ) : !logs.length ? (
          <div className="tp-view-empty">No work logs found for this tender.</div>
        ) : (
          <div className="tp-view-table-wrap">
            <table className="tp-view-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Start</th>
                  <th>End</th>
                  <th>Hours</th>
                  <th>Note</th>
                  <th>User</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log, idx) => (
                  <tr key={log.id || `${formatLogDate(log)}-${idx}`}>
                    <td>{formatLogDate(log)}</td>
                    <td>{formatClock(log.start_time)}</td>
                    <td>{formatClock(log.end_time)}</td>
                    <td>{formatLogHours(log)}</td>
                    <td className="tp-view-note">{log.description || log.note || "—"}</td>
                    <td>{formatLogUser(log)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="tp-modal-foot">
        <div className="tp-total">
          <span>Entries</span>
          <strong>{summary.total_worklogs || logs.length}</strong>
        </div>
        <div className="tp-total">
          <span>Total hours</span>
          <strong>{Number(summary.total_hours || 0).toFixed(2)} hrs</strong>
        </div>
        <div className="tp-foot-actions">
          <button type="button" className="tp-btn tp-btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </Modal>
  );
};

export default ViewWorkLogModal;
